import { Controller } from "@hotwired/stimulus"

// An animated product preview on the marketing pages (app/views/www/mocks).
// The markup rests in its finished state. While the preview is on screen this
// replays its build-up every `cycle` seconds: the .wm-play class starts the
// CSS animations, numbers count up and the search text types itself. With
// reduced motion it does nothing, and the finished state stands.
export default class extends Controller {
  static targets = ["count", "typed"]
  static values = { cycle: { type: Number, default: 10 } }

  connect() {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.start()
      else this.stop()
    }, { threshold: 0.35 })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.stop()
    this.rest()
  }

  start() {
    if (this.timer) return
    this.play()
    this.timer = setInterval(() => this.play(), this.cycleValue * 1000)
  }

  stop() {
    clearInterval(this.timer)
    this.timer = null
  }

  play() {
    this.element.classList.remove("wm-play")
    void this.element.offsetWidth // restart the CSS animations
    this.element.classList.add("wm-play")
    this.countTargets.forEach((el) => this.countUp(el))
    this.typedTargets.forEach((el) => this.type(el))
  }

  // Back to the finished state, so a Turbo snapshot never caches a half-built
  // preview.
  rest() {
    clearInterval(this.typing)
    this.element.classList.remove("wm-play")
    this.countTargets.forEach((el) => { el.textContent = el.dataset.count })
    this.typedTargets.forEach((el) => { el.textContent = el.dataset.text })
  }

  countUp(el) {
    const end = parseFloat(el.dataset.count)
    const decimals = Number(el.dataset.decimals || 0)
    const began = performance.now()
    const tick = (now) => {
      if (!this.timer) return
      const progress = Math.min(1, Math.max(0, (now - began) / 1600))
      el.textContent = (end * (1 - Math.pow(1 - progress, 3))).toFixed(decimals)
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  type(el) {
    const text = el.dataset.text
    let shown = 0
    clearInterval(this.typing)
    el.textContent = ""
    this.typing = setInterval(() => {
      el.textContent = text.slice(0, ++shown)
      if (shown >= text.length) clearInterval(this.typing)
    }, 110)
  }
}
